import * as React from 'react';
import { Button, Table, Input, List, Header, Divider, Segment, Message } from "semantic-ui-react";
import { Link } from "react-router-dom";
import 'katex/dist/katex.min.css';

const gamma = require('gamma')
const { InlineMath, BlockMath } = require('react-katex')

interface Props {
}

interface State {
  v: any[],
  m: number,
  k: number,
  t: number,
  dirichlet: number,
}

class Dirichlet extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = {
      v: [
        {
          alpha: 0.5,
          r: 4
        },
        {
          alpha: 0.3,
          r: 2
        },
        {
          alpha: 0.2,
          r: 1
        },
      ],
      m: 0.4125,
      k: 0.089,
      t: 1,
      dirichlet: 0.00021971305951817998,
    }

    this.add_v = this.add_v.bind(this);
    this.alpha_handleInput = this.alpha_handleInput.bind(this);
    this.r_handleInput = this.r_handleInput.bind(this);
    this.m_handleInput = this.m_handleInput.bind(this);
    this.k_handleInput = this.k_handleInput.bind(this);
    this.t_handleInput = this.t_handleInput.bind(this);
    this.get_dirichlet = this.get_dirichlet.bind(this);
    this.init_variables = this.init_variables.bind(this);
  }

  add_v() {
    var temp = this.state.v

    temp.push({
      alpha: 0.1,
      r: 1
    })

    this.setState({
      v: temp
    });
  }

  delete_v() {

  }

  alpha_handleInput(event: React.FormEvent<HTMLInputElement>) {
    event.preventDefault();
    var temp = this.state.v
    temp[Number(event.currentTarget.name) - 1].alpha = Number(event.currentTarget.value)
    this.setState({
      v: temp
    })
  }

  r_handleInput(event: React.FormEvent<HTMLInputElement>) {
    event.preventDefault();
    var temp = this.state.v

    temp[Number(event.currentTarget.name) - 1].r = Number(event.currentTarget.value)
    this.setState({
      v: temp
    })
  }

  m_handleInput(event: React.FormEvent<HTMLInputElement>) {
    event.preventDefault();
    this.setState({
      m: Number(event.currentTarget.value)
    })
  }

  k_handleInput(event: React.FormEvent<HTMLInputElement>) {
    event.preventDefault();
    this.setState({
      k: Number(event.currentTarget.value)
    })
  }

  t_handleInput(event: React.FormEvent<HTMLInputElement>) {
    event.preventDefault();
    this.setState({
      t: Number(event.currentTarget.value)
    })
  }

  get_dirichlet() {

    var S = this.state.v.reduce((a, x) => a += x.alpha, 0)
    var R = this.state.v.reduce((a, x) => a += x.r, 0)

    var gamma_1 = this.state.v.reduce((a, x) => a *= gamma(x.r + x.alpha), 1)
    var gamma_2 = this.state.v.reduce((a, x) => a *= gamma(x.alpha), 1)
    var gamma_3 = this.state.v.reduce((a, x) => a *= x.r, 1)

    var part_1 = gamma(S) * gamma_1 * gamma(R + this.state.k) / gamma_2 / gamma(S + R) / gamma_3 / gamma(this.state.k)
    var part_2 = (1 + this.state.k / this.state.m / this.state.t) ** (-1 * R)
    var part_3 = (1 + this.state.m * this.state.t / this.state.k) ** (-1 * this.state.k)

    var _dirichlet = part_1 * part_2 * part_3

    this.setState({
      dirichlet: _dirichlet
    })
  }

  init_variables() {

    this.setState({
      v: [
        {
          alpha: 0.5,
          r: 4
        },
        {
          alpha: 0.3,
          r: 2
        },
        {
          alpha: 0.2,
          r: 1
        },
      ],
      m: 0.4125,
      k: 0.089,
      t: 1,
      dirichlet: 0.00021971305951817998,
    })
  }

  render() {
    return (
      <div>

        <Header as="h3">デリシュレーのNBDモデル</Header>
        <Divider />

        デリシュレーのNBDモデルは<a href="https://ja.wayama.io/article/mkt/1/#3">こちら</a>より、以下の様に表現できます。

        <BlockMath>{`
          \\begin{aligned}
          &P(R, r_1, \\cdots, r_g) \\\\ &=\\frac{\\Gamma\\left(S \\right)}{\\displaystyle \\prod_{i=1}^{N} \\Gamma\\left(\\alpha_j \\right)} \\cdot \\frac{\\displaystyle \\prod_{i=1}^{N} \\Gamma\\left(r_j + \\alpha_j \\right)}{\\Gamma\\left(S+R \\right)} \\cdot \\frac{1}{\\displaystyle \\prod_{j=1}^{g} r_j!} \\frac{\\Gamma\\left(R + K \\right)}{\\displaystyle \\Gamma\\left(K \\right)} \\cdot \\left(1 + \\frac{K}{MT} \\right)^{-R} \\cdot\\left(1 + \\frac{MT}{K} \\right)^{-K}
          \\end{aligned}
        `}</BlockMath>


        <Header as="h3">パラメタの入力</Header>

        <Header as="h4"><InlineMath>{`r`}</InlineMath>と<InlineMath>{`r`}</InlineMath>の入力</Header>

        <Table celled textAlign='center'>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell></Table.HeaderCell>
              <Table.HeaderCell><InlineMath>{`\\alpha`}</InlineMath></Table.HeaderCell>
              <Table.HeaderCell></Table.HeaderCell>
              <Table.HeaderCell><InlineMath>{`r`}</InlineMath></Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {this.state.v.map((v, i) => {
              return (
                <Table.Row>
                  <Table.Cell><InlineMath>{`\\alpha_{${i + 1}}`}</InlineMath></Table.Cell>
                  <Table.Cell><Input onChange={this.alpha_handleInput} placeholder={v.alpha} name={(i + 1).toString()} /></Table.Cell>
                  <Table.Cell><InlineMath>{`r_{${i}}`}</InlineMath></Table.Cell>
                  <Table.Cell><Input onChange={this.r_handleInput} placeholder={v.r} name={(i + 1).toString()} /></Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>

          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell><InlineMath>{`\\sum_{i} \\alpha_i = `}</InlineMath>{this.state.v.reduce((a, x) => a += x.alpha, 0)} </Table.HeaderCell>
              <Table.HeaderCell />
              <Table.HeaderCell><InlineMath>{`R = \\sum_{i} r_i = `}</InlineMath>{this.state.v.reduce((a, x) => a += x.r, 0)} </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>

        {/* {this.state.v.reduce((a, x) => a += x.alpha, 0) !== 1 &&
          <Message
            negative
            header='alpha'
            content="alphaの合計値が1になるように入力してください。"
          />
        } */}

        {/* 行の追加はまだしない
        <div style={{ textAlign: "center" }}>
          <Button value="追加" onClick={this.add_v}>行の追加</Button>
          <Button value="追加" onClick={this.delete_v}>行の削除</Button>
        </div> */}


        <Header as="h4"><InlineMath>{`M,T,k`}</InlineMath>の入力</Header>
        <Table celled textAlign='center'>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell><InlineMath>{`M`}</InlineMath></Table.HeaderCell>
              <Table.HeaderCell><InlineMath>{`T`}</InlineMath></Table.HeaderCell>
              <Table.HeaderCell><InlineMath>{`k`}</InlineMath></Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            <Table.Row>
              <Table.Cell><Input onChange={this.m_handleInput} placeholder='0.4125' /></Table.Cell>
              <Table.Cell><Input onChange={this.t_handleInput} placeholder='1' /></Table.Cell>
              <Table.Cell><Input onChange={this.k_handleInput} placeholder='0.089' /></Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>


        <div style={{ textAlign: "center" }}>
          <Button value="計算実行" onClick={this.get_dirichlet}>計算実行</Button>
          <Button value="初期化" onClick={this.init_variables}>変数初期化</Button>
        </div>

        <Header as="h3">計算結果</Header>
        <Divider />

        <InlineMath>{`P(R, r_1, \\cdots, r_g)`}</InlineMath> = {this.state.dirichlet.toExponential(3)}

      </div>
    );
  }
}

export default Dirichlet;
